import { createContext, useContext } from 'react';
import { initialMatcherForm } from './MatcherData';
import { Answer, MatcherAnswers, QuestionId, StepId } from '../flow/types';
import { buildMatcherSearchKey } from '../flow/buildSearchTerms';
import { MatchedPro } from '../../../shared/types';

export type MatchSearchStatus = 'idle' | 'loading' | 'success' | 'error';

// searchKey (see buildMatcherSearchKey) is what lets MatcherSearchStep tell "this is the same
// search we already ran/started for these exact answers" from "the fan changed something
// since" (see hasAlreadyFoundMatchFor below) - exposed here rather than kept private to the
// provider's own dedup guard.
export interface MatchSearchState {
  status: MatchSearchStatus;
  searchKey: string | null;
  topMatch: MatchedPro | null;
  errorMessage: string;
}

export const INITIAL_MATCH_SEARCH_STATE: MatchSearchState = {
  status: 'idle',
  searchKey: null,
  topMatch: null,
  errorMessage: ''
};

export type ReferralCreationStatus = 'idle' | 'creating' | 'error';

export interface ReferralCreationState {
  status: ReferralCreationStatus;
  errorMessage: string;
}

export const INITIAL_REFERRAL_CREATION_STATE: ReferralCreationState = {
  status: 'idle',
  errorMessage: ''
};

interface MatcherContextData {
  matcherAnswers: MatcherAnswers;
  updateMatcherAnswer: (questionId: QuestionId, answer: Answer) => void;
  stepQueue: StepId[];
  phoneVerificationCountDown: number;
  updatePhoneVerificationCountDown: (countDown: number) => void;
  verificationToken: string;
  updateVerificationToken: (token: string) => void;
  matchSearchState: MatchSearchState;
  runMatchSearch: (
    postalCode: string,
    answers: MatcherAnswers
  ) => Promise<void>;
  referralCreation: ReferralCreationState;
  // Returns false without doing anything if a creation is already in flight, so callers only
  // advance the wizard for the submit that actually started it.
  createMatcherReferral: (email: string | null) => boolean;
  retryReferralCreation: () => void;
  // No-ops unless referralCreation.status is 'error' - clears a stale failure without starting
  // a new attempt.
  clearReferralCreationError: () => void;
}

// This default is only reached by a consumer rendered without a MatcherContextProvider
// ancestor, which never happens in the real app - MatcherContextProvider always supplies the
// real, useMemo-computed stepQueue. Left as [] rather than calling buildStepQueue() here since
// there's no real value in computing it for a path that's never actually taken.
const MatcherContext = createContext<MatcherContextData>({
  matcherAnswers: initialMatcherForm().matcherAnswers,
  updateMatcherAnswer: () => {
    return;
  },
  stepQueue: [],
  phoneVerificationCountDown: 0,
  // eslint-disable-next-line
  updatePhoneVerificationCountDown: (_) => {},
  verificationToken: '',
  updateVerificationToken: () => {},
  matchSearchState: INITIAL_MATCH_SEARCH_STATE,
  // eslint-disable-next-line
  runMatchSearch: (_postalCode, _answers) => Promise.resolve(),
  referralCreation: INITIAL_REFERRAL_CREATION_STATE,
  // eslint-disable-next-line
  createMatcherReferral: (_email) => false,
  retryReferralCreation: () => {},
  clearReferralCreationError: () => {}
});

function useMatcherContext() {
  return useContext(MatcherContext);
}

// Lets MatcherLocationStep decide, right as the fan submits a zip, whether to skip
// MatcherSearchStep entirely (a match already exists for this exact zip and these answers) or
// send them through it as usual.
function hasAlreadyFoundMatchFor(
  matchSearchState: MatchSearchState,
  postalCode: string,
  answers: MatcherAnswers
): boolean {
  return (
    matchSearchState.status === 'success' &&
    matchSearchState.searchKey === buildMatcherSearchKey(postalCode, answers)
  );
}

export { MatcherContext, useMatcherContext, hasAlreadyFoundMatchFor };
